import { Component, OnInit, Inject, signal, computed } from '@angular/core';
import {
  MatDialog,
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogClose,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { AddressValidationService } from 'src/app/services/address-validation.service';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  or,
  and,
  limit,
  deleteDoc,
  updateDoc,
} from 'firebase/firestore';
import { MatSnackBar } from '@angular/material/snack-bar';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { valueToDate } from 'src/app/globals';
import {
  GenericWarningDialogData,
  Product,
  AutomaticallyPlannedRide,
  Particularity,
  UserInRide,
  FailedAddress,
} from 'src/app/interfaces';
import {
  calculateOverlapPercentage,
  calculateSeatsTaken,
  checkUserDoubleBooking,
  checkUserDoubleBookingAutomaticRides,
  getFullLocationString,
  onMapsClick,
} from '../../helpers/ride-helper';
import {
  getUnchangedRideCoordinates,
  hasMatchingAddressCoordinates,
  isValidCoordinates,
  resolveLocationCoordinates,
} from '../../helpers/ride-coordinate-resolver';
import { dataStore, db } from 'src/app/app.component';
import { PlanType } from 'src/app/enums';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import { CopyRideComponent } from '../copy-ride/copy-ride.component';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatSuffix, MatError, MatHint } from '@angular/material/form-field';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { OrsAddressAutocompleteComponent } from 'src/app/components/ors-address-autocomplete/ors-address-autocomplete.component';
import { onSnapshot, orderBy } from 'firebase/firestore';
import { NgClass } from '@angular/common';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { DeleteRideComponent } from '../edit-ride/dialogs/delete-ride/delete-ride.component';
import { CopyUpdateComponent } from '../copy-update/copy-update.component';

export interface Address {
  id?: string;
  name: string;
  address: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  coordinatesFor?: string;
}

export interface DialogData {
  ride: AutomaticallyPlannedRide;
  rideId: string;
  excludeRideId?: string;
  isRetour?: boolean;
}

@Component({
  selector: 'app-edit-automatic-ride',
  templateUrl: './edit-automatic-ride.component.html',
  styleUrls: ['./edit-automatic-ride.component.scss'],
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatTooltip,
    MatIcon,
    MatDialogClose,
    CdkScrollable,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatError,
    MatHint,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatSlideToggle,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatChipInput,
    MatAutocomplete,
    MatAutocompleteTrigger,
    OrsAddressAutocompleteComponent,
    NgClass,
  ],
})
export class EditAutomaticRideComponent implements OnInit {
  rideId: string;
  newRide: boolean;
  rideForm: UntypedFormGroup;
  organisationId = dataStore.selectedOrganisation.id;
  useCharacteristics = dataStore.generalSettings.characteristicsStatus;
  products = signal<Product[]>([]);
  particularities: Particularity[] = dataStore.selectedOrganisation.particularities ?? [];
  characteristics = signal<string[]>([]);
  saving = signal(false);
  loading = signal(true);
  capacityMeter = signal<number>(0);
  totalSeatsInAllVehicles: number = 0;
  savedRide: AutomaticallyPlannedRide;
  rideDuration = dataStore.generalSettings.customRideDuration ?? 15;
  hours = signal<number[]>([]);
  minutes = signal<{ val: number; text: string }[]>([]);
  minuteStep = 5;
  isFormValid() {
    if (this.useCharacteristics && this.characteristics().length === 0) return false;
    return !!this.rideForm && this.rideForm.valid && !!this.passenger();
  }

  isSaveDisabled() {
    return !this.isFormValid() || this.saving();
  }
  getFullLocationString = getFullLocationString;
  onMapsClick = onMapsClick;
  capacityStatus = computed(() => {
    const val = this.capacityMeter();
    if (val <= 0.45) return 'Rustig';
    if (val <= 0.75) return 'Redelijk druk';
    return 'Druk';
  });
  capacityMeterBg = computed(() => {
    const val = this.capacityMeter();
    if (val <= 0.45) return '#e8f5e9'; // Light Green
    if (val <= 0.75) return '#fff3e0'; // Light Orange
    return '#ffebee'; // Light Red
  });

  locationTypes = [
    { val: 'home', name: 'Thuisadres gebruiken' },
    { val: 'custom', name: 'Adres handmatig invullen' },
    { val: 'addressFromDb', name: 'Standaardlocatie gebruiken' },
    { val: 'recent', name: 'Laatste adressen gebruiken' },
  ];

  addresses: Address[] = [];
  addressesFrom = signal<Address[]>([]);
  addressesTo = signal<Address[]>([]);
  nothingFound = signal(false);

  fromHidden = signal(false);
  standardFromHidden = signal(true);
  recentFromHidden = signal(true);
  toHidden = signal(false);
  standardToHidden = signal(true);
  recentToHidden = signal(true);
  recentDestinations = signal<string[]>([]);
  characteristicCtrl = new UntypedFormControl();
  allCharacteristics = signal<string[]>([]);
  filteredCharacteristics = signal<string[]>([]);

  searchCtrl = new UntypedFormControl(null);
  users: UserInRide[] = [];
  filteredUsers = signal<UserInRide[]>([]);
  passenger = signal<any>(null);

  readonly separatorKeysCodes = [COMMA, ENTER, SPACE] as const;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private fb: UntypedFormBuilder,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<EditAutomaticRideComponent>,
    private snackBar: MatSnackBar,
    private addressValidationService: AddressValidationService,
  ) {}

  async ngOnInit() {
    this.getSettings();
    this.searchCtrl.setValidators(() => {
      return !this.passenger() ? { passengerRequired: true } : null;
    });
    this.searchCtrl.updateValueAndValidity();
    if (this.data.ride.id) {
      this.rideId = this.data.ride.id;
      this.newRide = false;
    } else {
      this.newRide = true;
      this.rideId = doc(collection(db, `organisations/${this.organisationId}/automaticRides`)).id;
    }

    if (this.data.isRetour && !this.data.ride.id) {
      this.rideId = doc(collection(db, `organisations/${this.organisationId}/automaticRides`)).id;
      this.newRide = true;
    }

    let start = valueToDate(this.data.ride.start);
    let end = valueToDate(this.data.ride.end);

    if (this.data.isRetour && this.newRide) {
      start = moment(end).add(dataStore.generalSettings.customRideDuration, 'minutes').toDate();
      end = moment(start).add(dataStore.generalSettings.customRideDuration, 'minutes').toDate();
    }

    const passengerData = this.data.ride.passenger;
    this.passenger.set(passengerData?.id ? passengerData : null);

    let fromLocation = passengerData?.from;
    if (passengerData?.fromType === 'addressFromDb') {
      fromLocation = passengerData?.from?.id ?? passengerData?.from;
    }

    let toLocation = passengerData?.to;
    if (passengerData?.toType === 'addressFromDb') {
      toLocation = passengerData?.to?.id ?? passengerData?.to;
    }

    this.searchCtrl.valueChanges.subscribe((value) => {
      if (!value || value === '') {
        this.filteredUsers.set([]);
        return;
      }

      const filtered = [];
      for (const user of this.users) {
        if (typeof value !== 'string') {
          continue;
        }
        if (this.checkFilters(user, value)) {
          filtered.push(user);
        }
      }
      this.filteredUsers.set(filtered);
    });

    this.getUsers();

    this.rideForm = this.fb.group({
      remarks: [passengerData?.remarks ?? ''],
      commentsForDriver: [passengerData?.commentsForDriver ?? ''],
      rideDate: [start, [Validators.required]],
      startHour: [start.getHours(), Validators.required],
      startMinute: [start.getMinutes(), Validators.required],
      endHour: [end.getHours(), Validators.required],
      endMinute: [end.getMinutes(), Validators.required],
      from: [fromLocation, Validators.required],
      fromType: [passengerData?.fromType ?? 'home', Validators.required],
      to: [toLocation, Validators.required],
      toType: [passengerData?.toType ?? 'addressFromDb', Validators.required],
      fromSearch: [],
      toSearch: [],
      particularityIds: [passengerData?.particularityIds ?? []],
      specifyDropoffTime: [this.data.ride.specifyDropoffTime ?? false],
    });

    await this.getAddresses();

    this.setAddressTypeVisibility('from', this.rideForm.value.fromType);
    this.setAddressTypeVisibility('to', this.rideForm.value.toType);

    if (this.rideForm.value.fromType === 'home') {
      this.rideForm.controls.from.disable();
    }
    if (this.rideForm.value.toType === 'home') {
      this.rideForm.controls.to.disable();
    }

    if (this.data.ride.characteristics) {
      this.characteristics.set([...this.data.ride.characteristics]);
    }

    await this.getProducts();
    await this.getCharacteristics();
    await this.getTotalSeats();
    await this.calculateCapacity();
    await this.getRecentDestinations();

    this.characteristicCtrl.valueChanges.subscribe((value) => {
      if (typeof value === 'string') {
        this.filteredCharacteristics.set(
          this.allCharacteristics().filter((characteristic) =>
            characteristic.toLowerCase().includes(value.toLowerCase()),
          ),
        );
      } else {
        this.filteredCharacteristics.set([...this.allCharacteristics()]);
      }
    });

    this.setupStandardLocationListener('fromSearch');
    this.setupStandardLocationListener('toSearch');

    this.rideForm.controls.specifyDropoffTime.valueChanges.subscribe(() =>
      this.calculateCapacity(),
    );

    setTimeout(() => {
      this.loading.set(false);
    });
  }

  async save() {
    if (this.saving() || !this.isFormValid()) return;
    this.saving.set(true);

    const formValue = this.rideForm.getRawValue();

    const start = new Date(formValue.rideDate);
    start.setHours(formValue.startHour, formValue.startMinute);
    let end = new Date(formValue.rideDate);
    if (formValue.specifyDropoffTime) {
      end.setHours(formValue.endHour, formValue.endMinute);
    } else {
      const calculatedEnd = moment(start).add(
        dataStore.generalSettings.customRideDuration,
        'minutes',
      );
      end = calculatedEnd.toDate();
    }

    start.setSeconds(0, 0);
    end.setSeconds(0, 0);

    const passenger = this.passenger();
    const from =
      formValue.fromType === 'addressFromDb'
        ? this.addresses.find((a) => a.id === formValue.from) || formValue.from
        : formValue.from;
    const to =
      formValue.toType === 'addressFromDb'
        ? this.addresses.find((a) => a.id === formValue.to) || formValue.to
        : formValue.to;

    console.log('from', from);
    console.log('to', to);

    const fromAddressStr = typeof from === 'string' ? from : from?.address || from?.name || '';
    const toAddressStr = typeof to === 'string' ? to : to?.address || to?.name || '';

    const fromCoordinatesMatch =
      typeof from === 'object' &&
      hasMatchingAddressCoordinates(from as Address, (address) =>
        this.addressValidationService.normalizeAddressForComparison(address),
      );
    const toCoordinatesMatch =
      typeof to === 'object' &&
      hasMatchingAddressCoordinates(to as Address, (address) =>
        this.addressValidationService.normalizeAddressForComparison(address),
      );
    const unchangedFromCoordinates = getUnchangedRideCoordinates(
      formValue.fromType,
      from,
      this.data.ride.passenger?.fromType,
      this.data.ride.passenger?.from,
      this.data.ride.fromLongitude,
      this.data.ride.fromLatitude,
      (address) => this.addressValidationService.normalizeAddressForComparison(address),
    );
    const unchangedToCoordinates = getUnchangedRideCoordinates(
      formValue.toType,
      to,
      this.data.ride.passenger?.toType,
      this.data.ride.passenger?.to,
      this.data.ride.toLongitude,
      this.data.ride.toLatitude,
      (address) => this.addressValidationService.normalizeAddressForComparison(address),
    );
    const addressFromResult = await resolveLocationCoordinates({
      locationType: formValue.fromType,
      directCoordinates: fromCoordinatesMatch ? (from as Address).coordinates : undefined,
      unchangedCoordinates: unchangedFromCoordinates,
      homeCoordinates: this.passenger()?.addressCoordinates,
      hasMatchingHomeCoordinates: this.hasMatchingPassengerCoordinates(),
      addressToValidate: fromAddressStr,
      sourceLabel: 'edit-automatic-ride:from',
      validateAddress: (address) => this.addressValidationService.validateAddress(address),
    });
    const addressToResult = await resolveLocationCoordinates({
      locationType: formValue.toType,
      directCoordinates: toCoordinatesMatch ? (to as Address).coordinates : undefined,
      unchangedCoordinates: unchangedToCoordinates,
      homeCoordinates: this.passenger()?.addressCoordinates,
      hasMatchingHomeCoordinates: this.hasMatchingPassengerCoordinates(),
      addressToValidate: toAddressStr,
      sourceLabel: 'edit-automatic-ride:to',
      validateAddress: (address) => this.addressValidationService.validateAddress(address),
    });

    const hasFromCoordinates =
      !!addressFromResult.coordinates && isValidCoordinates(addressFromResult.coordinates);
    const hasToCoordinates =
      !!addressToResult.coordinates && isValidCoordinates(addressToResult.coordinates);
    const fromAddressAccepted = addressFromResult.isValid || addressFromResult.requiresConfirmation;
    const toAddressAccepted = addressToResult.isValid || addressToResult.requiresConfirmation;

    if (
      (addressFromResult.requiresConfirmation &&
        !(await this.confirmLowConfidenceAddress(addressFromResult, fromAddressStr))) ||
      (addressToResult.requiresConfirmation &&
        !(await this.confirmLowConfidenceAddress(addressToResult, toAddressStr)))
    ) {
      this.saving.set(false);
      return;
    }

    if (!fromAddressAccepted || !toAddressAccepted || !hasFromCoordinates || !hasToCoordinates) {
      const failedAddresses: FailedAddress[] = [];
      if (!fromAddressAccepted || !hasFromCoordinates) {
        failedAddresses.push({
          name: 'Ophaaladres',
          address: getFullLocationString(from),
          error: !fromAddressAccepted ? 'Niet geldig' : 'Geen coördinaten beschikbaar',
        });
      }
      if (!toAddressAccepted || !hasToCoordinates) {
        failedAddresses.push({
          name: 'Afzetadres',
          address: getFullLocationString(to),
          error: !toAddressAccepted ? 'Niet geldig' : 'Geen coördinaten beschikbaar',
        });
      }
      this.dialog.open(WarningDialog, {
        width: '600px',
        data: { dialogType: 'addressNotValid', optionalData: { failedAddresses } },
      });
      this.saving.set(false);
      return;
    }

    await this.cacheStandardLocationCoordinates(
      formValue.fromType,
      from,
      addressFromResult.coordinates,
    );
    await this.cacheStandardLocationCoordinates(formValue.toType, to, addressToResult.coordinates);
    await this.cacheUserAddressCoordinates(formValue.fromType, addressFromResult.coordinates);
    await this.cacheUserAddressCoordinates(formValue.toType, addressToResult.coordinates);

    const defaultProductDoc = await getDocs(
      query(
        collection(db, `organisations/${this.organisationId}/products`),
        where('default', '==', true),
        limit(1),
      ),
    );
    const defaultProduct =
      defaultProductDoc.docs.length > 0
        ? {
            id: defaultProductDoc.docs[0].id,
            ...defaultProductDoc.docs[0].data(),
          }
        : null;

    const showDoubleBookingWarning =
      dataStore.generalSettings?.doubleBookingActivated !== null
        ? !dataStore.generalSettings.doubleBookingActivated
        : true;

    if (showDoubleBookingWarning) {
      const rideDates = {
        start: moment(start),
        end: moment(end),
      };

      const passengerToCheck = {
        ...passenger,
        userPickupTime: start,
        userDropoffTime: end,
      };

      let doubleBookedManual = await checkUserDoubleBooking(
        passengerToCheck as any,
        rideDates,
        this.rideId,
        this.organisationId,
        this.data.excludeRideId,
      );

      let doubleBookedAutomatic = await checkUserDoubleBookingAutomaticRides(
        passengerToCheck as any,
        rideDates,
        this.rideId,
        this.organisationId,
        this.data.excludeRideId,
      );

      // Fallback for identical time slots (which '()' exclusive check misses)
      if (!doubleBookedManual || !doubleBookedAutomatic) {
        const qManual = query(
          collection(db, `organisations/${this.organisationId}/rides`),
          where('userIds', 'array-contains', passenger.id),
          where('day', '==', start.getDate()),
          where('month', '==', start.getMonth()),
          where('year', '==', start.getFullYear()),
        );
        const qAuto = query(
          collection(db, `organisations/${this.organisationId}/automaticRides`),
          where('passenger.id', '==', passenger.id),
          where('planningCompleted', '==', false),
        );

        const [snapManual, snapAuto] = await Promise.all([getDocs(qManual), getDocs(qAuto)]);

        const isIdentical = (ride: any, id: string) => {
          if (id === this.rideId || id === this.data.excludeRideId) return false;
          const rStart = moment(ride.start.toDate());
          const rEnd = moment(ride.end.toDate());
          return rStart.isSame(rideDates.start) && rEnd.isSame(rideDates.end);
        };

        if (!doubleBookedManual) {
          doubleBookedManual = snapManual.docs.some((doc) => isIdentical(doc.data(), doc.id));
        }
        if (!doubleBookedAutomatic) {
          doubleBookedAutomatic = snapAuto.docs.some((doc) => isIdentical(doc.data(), doc.id));
        }
      }

      if (doubleBookedManual || doubleBookedAutomatic) {
        const result = await firstValueFrom(
          this.dialog
            .open(WarningDialog, {
              width: '600px',
              data: {
                dialogType: 'userInRide',
              } as GenericWarningDialogData,
            })
            .afterClosed(),
        );

        if (!result) {
          this.saving.set(false);
          return;
        }
      }
    }

    const passengerForSave = {
      ...passenger,
      from: from,
      fromType: formValue.fromType,
      to: to,
      toType: formValue.toType,
      remarks: formValue.remarks ?? '',
      commentsForDriver: formValue.commentsForDriver ?? '',
      particularityIds: formValue.particularityIds,
      rideProduct: defaultProduct ?? null,
      userDropoffTime: end,
      userPickupTime: start,
      vehicleEntryDuration: dataStore.generalSettings.vehicleEntryDuration ?? 0,
      vehicleExitDuration: dataStore.generalSettings.vehicleExitDuration ?? 0,
    };

    Object.keys(passengerForSave).forEach((key) => {
      if (passengerForSave[key] === undefined) {
        delete passengerForSave[key];
      }
    });

    const saveObj = {
      start: start,
      end: end,
      fromLongitude: addressFromResult.coordinates.longitude,
      fromLatitude: addressFromResult.coordinates.latitude,
      toLongitude: addressToResult.coordinates.longitude,
      toLatitude: addressToResult.coordinates.latitude,
      useBuffers: true,
      passenger: passengerForSave,
      characteristics: this.characteristics(),
      rejected: false,
      planningCompleted: false,
      retourParentId: this.data.ride.retourParentId ?? null,
      isRetour: this.data.isRetour ?? false,
      parentRideId: this.data.ride.parentRideId ?? null,
      hasCopy: this.data.ride.hasCopy ?? null,
      specifyDropoffTime: formValue.specifyDropoffTime,
    };

    if (this.data.ride.parentRideId) {
      const dialogRef = this.dialog.open(CopyUpdateComponent, {
        width: '600px',
        data: {
          oldRide: structuredClone(this.data.ride),
          newRide: structuredClone(saveObj),
          planType: PlanType.Automatic,
        },
      });
      dialogRef.afterClosed().subscribe(async (result) => {
        if (result === 'done') {
          await this.saveRide(saveObj);
        } else if (result === false) {
          (saveObj as any).parentRideId = null;
          await this.saveRide(saveObj);
        } else {
          this.saving.set(false);
        }
      });
    } else {
      await this.saveRide(saveObj);
    }
  }

  private async confirmLowConfidenceAddress(
    addressValidationResult: Awaited<ReturnType<AddressValidationService['validateAddress']>>,
    enteredAddress: string,
  ): Promise<boolean> {
    return await firstValueFrom(
      this.dialog
        .open(WarningDialog, {
          width: '600px',
          data: {
            dialogType: 'addressLowConfidence',
            optionalData: {
              enteredAddress,
              candidateAddress: [
                addressValidationResult.street,
                addressValidationResult.housenumber,
                addressValidationResult.postal,
                addressValidationResult.city,
              ]
                .filter(Boolean)
                .join(' '),
              coordinates: addressValidationResult.coordinates,
            },
          },
        })
        .afterClosed(),
    );
  }

  private async cacheStandardLocationCoordinates(
    addressType: string,
    address: unknown,
    coordinates?: { latitude: number; longitude: number },
  ): Promise<void> {
    if (
      addressType !== 'addressFromDb' ||
      typeof address !== 'object' ||
      !address ||
      !coordinates
    ) {
      return;
    }

    const standardLocation = address as {
      id?: string;
      address?: string;
      coordinates?: unknown;
    };
    if (!standardLocation.id || standardLocation.coordinates) {
      return;
    }

    try {
      await updateDoc(
        doc(db, `organisations/${this.organisationId}/addresses/${standardLocation.id}`),
        {
          coordinates,
          coordinatesFor: this.addressValidationService.normalizeAddressForComparison(
            standardLocation.address ?? '',
          ),
        },
      );
      console.info('[CoordinateResolver] Saved coordinates to standard location document', {
        sourceLabel: 'edit-automatic-ride',
        organisationId: this.organisationId,
        addressId: standardLocation.id,
        address: standardLocation.address ?? '',
        coordinates,
      });
    } catch (error) {
      // Best-effort cache write; failures here should not abort saving the ride.
      console.warn('[CoordinateResolver] Failed to cache standard location coordinates', error);
    }
  }

  private async cacheUserAddressCoordinates(
    addressType: string,
    coordinates?: { latitude: number; longitude: number },
  ): Promise<void> {
    const passenger = this.passenger();
    if (addressType !== 'home' || !passenger?.id || !coordinates || passenger.addressCoordinates) {
      return;
    }

    try {
      await updateDoc(doc(db, `organisations/${this.organisationId}/users/${passenger.id}`), {
        addressCoordinates: coordinates,
        addressCoordinatesFor: this.addressValidationService.normalizeAddressForComparison(
          passenger.address,
        ),
      });
      console.info('[CoordinateResolver] Saved home coordinates to user document', {
        sourceLabel: 'edit-automatic-ride',
        organisationId: this.organisationId,
        userId: passenger.id,
        address: passenger.address,
        coordinates,
      });
    } catch (error) {
      // Best-effort cache write; failures here should not abort saving the ride.
      console.warn('[CoordinateResolver] Failed to cache user home coordinates', error);
      return;
    }
    this.passenger.set({
      ...passenger,
      addressCoordinates: coordinates,
      addressCoordinatesFor: this.addressValidationService.normalizeAddressForComparison(
        passenger.address,
      ),
    });
  }

  private hasMatchingPassengerCoordinates(): boolean {
    const passenger = this.passenger();
    if (!passenger?.addressCoordinates) {
      return false;
    }

    if (!passenger.addressCoordinatesFor) {
      return true;
    }

    return (
      passenger.addressCoordinatesFor ===
      this.addressValidationService.normalizeAddressForComparison(passenger.address)
    );
  }

  async saveRide(saveObj: any) {
    const rideRef = doc(db, `organisations/${this.organisationId}/automaticRides/${this.rideId}`);
    await setDoc(rideRef, saveObj, { merge: true });

    // Update parent if this is a new retour
    if (this.newRide && saveObj.isRetour && saveObj.retourParentId) {
      const parentRef = doc(
        db,
        `organisations/${this.organisationId}/automaticRides/${saveObj.retourParentId}`,
      );
      await setDoc(parentRef, { retourChildId: this.rideId }, { merge: true });
    }

    this.savedRide = { ...saveObj, id: this.rideId } as any;
    this.saving.set(false);
    this.snackBar.open('Voorbereide rit opgeslagen', 'X', {
      duration: 3000,
    });
  }

  async dateUpdated(type: 'start' | 'end' | 'date', newDate?: Date) {
    const patchObj = {};
    const formValue = this.rideForm.getRawValue();

    if (type === 'start') {
      const startDate = new Date(formValue.rideDate);
      startDate.setHours(formValue.startHour, formValue.startMinute);
      const endDate = new Date(formValue.rideDate);
      endDate.setHours(formValue.endHour, formValue.endMinute);

      if (startDate >= endDate) {
        const newEndDate = moment(startDate).add(this.rideDuration, 'minutes').toDate();
        patchObj['endHour'] = newEndDate.getHours();
        patchObj['endMinute'] = newEndDate.getMinutes();
      }
    } else if (type === 'end') {
      const startDate = new Date(formValue.rideDate);
      startDate.setHours(formValue.startHour, formValue.startMinute);
      const endDate = new Date(formValue.rideDate);
      endDate.setHours(formValue.endHour, formValue.endMinute);

      if (endDate <= startDate) {
        const newEndDate = moment(startDate).add(this.rideDuration, 'minutes').toDate();
        patchObj['endHour'] = newEndDate.getHours();
        patchObj['endMinute'] = newEndDate.getMinutes();
      }
    } else if (type === 'date' && newDate) {
      patchObj['rideDate'] = newDate;
    }

    if (Object.keys(patchObj).length > 0) {
      this.rideForm.patchValue(patchObj);
    }

    await this.calculateCapacity();
  }

  getSettings() {
    const startHour = dataStore.generalSettings?.daypartSettings?.startHour ?? 6;
    const endHour = dataStore.generalSettings?.daypartSettings?.endHour ?? 24;
    const hours: number[] = [];
    for (let i = startHour; i <= endHour; i++) {
      hours.push(i);
    }
    this.hours.set(hours);

    const minutes: { val: number; text: string }[] = [];
    for (let i = 0; i < 60; i += this.minuteStep) {
      minutes.push({
        val: i,
        text: `0${i}`.slice(-2),
      });
    }
    this.minutes.set(minutes);
  }

  getUsers() {
    onSnapshot(
      query(
        collection(db, `organisations/${this.organisationId}/users`),
        where(`passenger`, `==`, true),
      ),
      (snapshot) => {
        this.users = snapshot.docs.map((doc) => {
          const user = { id: doc.id, ...doc.data() } as UserInRide;
          return user;
        });
      },
    );
  }

  checkFilters(item: any, searchQuery: string) {
    const isSuperUser = dataStore.rootUser?.rights === 'admin';
    if (item.hidden || (item.superuser && !isSuperUser)) {
      return false;
    }

    searchQuery = searchQuery.toLowerCase();

    const passesName =
      item.name?.toLowerCase().includes(searchQuery) ||
      item.title?.toLowerCase().includes(searchQuery);
    const passesAddress =
      item.address?.toLowerCase().includes(searchQuery) ||
      item.postal?.toLowerCase().includes(searchQuery) ||
      item.streetName?.toLowerCase().includes(searchQuery);

    return passesName || passesAddress;
  }

  async selectUser(user: UserInRide) {
    if (typeof user === 'object') {
      const userObj: any = {
        id: user.id,
        title: user.title ?? user.name,
        email: user.email,
        phoneNumber: user.phoneNumber ?? user.phone ?? '',
      };

      userObj['address'] = this.getUserAddress(user);
      userObj['addressCoordinates'] = user.addressCoordinates;
      userObj['addressCoordinatesFor'] = user.addressCoordinatesFor;
      userObj['particularityIds'] = user.particularityIds ?? [];
      userObj['remarks'] = user.remarks;
      userObj['commentsForDriver'] = user.commentsForDriver;

      this.passenger.set(userObj);

      this.searchCtrl.updateValueAndValidity();

      this.rideForm.patchValue({
        particularityIds: userObj.particularityIds,
        remarks: userObj.remarks,
        commentsForDriver: userObj.commentsForDriver,
        from: userObj.address ? userObj.address : this.rideForm.value.from,
        fromType: userObj.address ? 'home' : 'custom',
      });

      if (userObj.address) {
        this.setAddressTypeVisibility('from', 'home');
        this.rideForm.controls.from.disable();
      } else {
        this.rideForm.controls.from.enable();
      }

      this.recentDestinations.set(user.recentDestinations ?? []);
    }
  }

  getUserAddress(user: any): string {
    if (!user || !user.streetName) return user?.address ?? '';
    if (user.houseNumberAddition) {
      return `${user.streetName} ${user.houseNumber}-${user.houseNumberAddition}, ${user.postal} ${user.city}`;
    }
    return `${user.streetName} ${user.houseNumber}, ${user.postal} ${user.city}`;
  }

  async getProducts() {
    const productsSnapshot = await getDocs(
      query(collection(db, `organisations/${this.organisationId}/products`)),
    );
    this.products.set(
      productsSnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }) as Product),
    );
  }

  async getTotalSeats() {
    const vehiclesSnapshot = await getDocs(
      query(
        collection(db, `organisations/${this.organisationId}/vehicles`),
        where('active', '==', true),
      ),
    );
    this.totalSeatsInAllVehicles = vehiclesSnapshot.docs.reduce(
      (total, doc) => total + (doc.data()['seats'] ?? 0),
      0,
    );
  }

  async getCharacteristics() {
    const allCharacteristics = (
      await getDocs(
        query(
          collection(db, `organisations/${this.organisationId}/characteristics`),
          where(`active`, `==`, true),
        ),
      )
    ).docs.map((doc) => doc.data().name);
    this.allCharacteristics.set(allCharacteristics);
    this.filteredCharacteristics.set(allCharacteristics);
  }

  addCharacteristic(event: any): void {
    const value = (event.value || '').trim();
    if (value) {
      const indexAll = this.allCharacteristics().indexOf(value);
      if (indexAll >= 0) {
        const index = this.characteristics().indexOf(value);
        if (index === -1) {
          this.characteristics.update((chars) => [...chars, value]);
        }
      }
    }
    event.chipInput!.clear();
    this.characteristicCtrl.setValue(null);
  }

  removeCharacteristic(characteristic: string): void {
    const index = this.characteristics().indexOf(characteristic);
    if (index >= 0) {
      this.characteristics.update((chars) => {
        const newChars = [...chars];
        newChars.splice(index, 1);
        return newChars;
      });
    }
  }

  selectedCharacteristic(event: MatAutocompleteSelectedEvent): void {
    if (this.characteristics().indexOf(event.option.viewValue) === -1) {
      this.characteristics.update((chars) => [...chars, event.option.viewValue]);
    }
    this.characteristicCtrl.setValue(null);
  }

  getRideData(isForRoute: boolean = false) {
    const formValue = this.rideForm.getRawValue();
    const passenger = this.passenger();

    if (!isForRoute && !passenger) return null;

    const start = new Date(formValue.rideDate);
    start.setHours(formValue.startHour, formValue.startMinute);
    let end = new Date(formValue.rideDate);
    if (formValue.specifyDropoffTime) {
      end.setHours(formValue.endHour, formValue.endMinute);
    } else {
      end = moment(start).add(dataStore.generalSettings.customRideDuration, 'minutes').toDate();
    }

    let fromLocation = formValue.from;
    if (formValue.fromType === 'addressFromDb') {
      const addr = this.addresses.find((a) => a.id === formValue.from);
      if (addr) fromLocation = addr;
    }

    let toLocation = formValue.to;
    if (formValue.toType === 'addressFromDb') {
      const addr = this.addresses.find((a) => a.id === formValue.to);
      if (addr) toLocation = addr;
    }

    const ride = {
      ...formValue,
      locationsOrder: [
        {
          userId: passenger?.id || (isForRoute ? 'temp' : null),
          type: 'from',
          time: start,
          location: fromLocation,
          fullLocation: getFullLocationString(fromLocation),
        },
        {
          userId: passenger?.id || (isForRoute ? 'temp' : null),
          type: 'to',
          time: end,
          location: toLocation,
          fullLocation: getFullLocationString(toLocation),
        },
      ],
    };

    if (!isForRoute && (!ride.locationsOrder[0].userId || !ride.locationsOrder[1].userId)) {
      return null;
    }

    return ride as any;
  }

  async calculateCapacity() {
    const formValue = this.rideForm.getRawValue();
    const start = new Date(formValue.rideDate);
    start.setHours(formValue.startHour, formValue.startMinute);
    let end = new Date(formValue.rideDate);
    if (formValue.specifyDropoffTime) {
      end.setHours(formValue.endHour, formValue.endMinute);
    } else {
      const calculatedEnd = moment(start).add(
        dataStore.generalSettings.customRideDuration,
        'minutes',
      );
      end = calculatedEnd.toDate();
    }

    let seatsTaken = 0;
    const automaticRidesQuery = query(
      collection(db, `organisations/${this.organisationId}/automaticRides`),
      and(
        where('planningCompleted', '==', false),
        or(
          and(where('start', '>=', start), where('start', '<=', end)),
          and(where('end', '>=', start), where('end', '<=', end)),
        ),
      ),
    );

    const automaticRidesSnapshot = await getDocs(automaticRidesQuery);
    automaticRidesSnapshot.forEach((doc) => {
      if (this.rideId !== doc.id) {
        const rideData = doc.data() as AutomaticallyPlannedRide;
        const overlap = calculateOverlapPercentage(
          start,
          end,
          rideData.start.toDate(),
          rideData.end.toDate(),
        );
        seatsTaken += calculateSeatsTaken(rideData, this.particularities) * overlap;
      }
    });

    const ridesQuery = query(
      collection(db, `organisations/${this.organisationId}/rides`),
      or(
        and(where('start', '>=', start), where('start', '<=', end)),
        and(where('end', '>=', start), where('end', '<=', end)),
      ),
    );

    const ridesSnapshot = await getDocs(ridesQuery);
    ridesSnapshot.forEach((doc) => {
      const rideData = doc.data() as any;
      const overlap = calculateOverlapPercentage(
        start,
        end,
        rideData.start.toDate(),
        rideData.end.toDate(),
      );
      const rideSeatsTaken =
        Array.isArray(rideData.users) && rideData.users.length > 0 ? rideData.users.length : 1;
      seatsTaken += rideSeatsTaken * overlap;
    });

    this.capacityMeter.set(
      this.totalSeatsInAllVehicles > 0 ? seatsTaken / this.totalSeatsInAllVehicles : 0,
    );
  }

  getAddresses() {
    return new Promise<void>((resolve) => {
      let first = true;
      onSnapshot(
        query(
          collection(db, `organisations/${this.organisationId}/addresses`),
          orderBy('name', 'asc'),
        ),
        (snapshot) => {
          this.addresses = snapshot.docs.map((doc) => {
            return { id: doc.id, ...doc.data() } as Address;
          });
          this.addressesTo.set([...this.addresses]);
          this.addressesFrom.set([...this.addresses]);

          // Fix standard addresses in form if needed
          if (this.rideForm) {
            const fromVal = this.rideForm.value.from;
            if (fromVal && this.rideForm.value.fromType === 'addressFromDb') {
              const addr = this.addresses.find((a) => a.id === fromVal);
              if (addr)
                this.rideForm.patchValue(
                  { fromSearch: addr.name + ', ' + addr.address },
                  { emitEvent: false },
                );
            }
            const toVal = this.rideForm.value.to;
            if (toVal && this.rideForm.value.toType === 'addressFromDb') {
              const addr = this.addresses.find((a) => a.id === toVal);
              if (addr)
                this.rideForm.patchValue(
                  { toSearch: addr.name + ', ' + addr.address },
                  { emitEvent: false },
                );
            }
          }

          if (first) {
            first = false;
            resolve();
          }
        },
      );
    });
  }

  async getRecentDestinations() {
    const passengerId = this.data.ride.passenger?.id;
    if (passengerId) {
      onSnapshot(
        doc(db, `organisations/${this.organisationId}/users/${passengerId}`),
        (snapshot) => {
          this.recentDestinations.set(snapshot.data()?.recentDestinations ?? []);
        },
      );
    }
  }

  setupStandardLocationListener(type: string) {
    this.rideForm.controls[type].valueChanges.subscribe((value) => {
      this.nothingFound.set(false);
      if (type === 'toSearch') {
        this.addressesTo.set(this.filterStandardAddress(value));
        this.nothingFound.set(this.addressesTo().length === 0);
      } else if (type === 'fromSearch') {
        this.addressesFrom.set(this.filterStandardAddress(value));
        this.nothingFound.set(this.addressesFrom().length === 0);
      }
    });
  }

  filterStandardAddress(value: string) {
    const filterValue = value?.toLowerCase() ?? '';
    return this.addresses.filter(
      (address) =>
        address.name.toLowerCase().includes(filterValue) ||
        address.address.toLowerCase().includes(filterValue),
    );
  }

  standardAddressSelected(event: any, type: 'from' | 'to') {
    const address = this.addresses.find((a) => a.id === event.option.value);
    if (address) {
      this.rideForm.patchValue({
        [type]: address.id,
        [type + 'Search']: address.name + ', ' + address.address,
      });
    }
  }

  toggleParticularity(id: string) {
    const ids = [...this.rideForm.value.particularityIds];
    const index = ids.indexOf(id);
    if (index === -1) {
      ids.push(id);
    } else {
      ids.splice(index, 1);
    }
    this.rideForm.patchValue({ particularityIds: ids });
  }

  onInputTypeChange(type: 'from' | 'to', value: any) {
    const val = typeof value === 'string' ? value : value.value;
    this.rideForm.controls[type].enable();
    const patchObj: any = {};
    if (val === 'custom') {
      patchObj[type] = '';
    } else if (val === 'home') {
      const homeAddress = this.passenger().address;
      if (homeAddress) {
        patchObj[type] = homeAddress;
        this.rideForm.controls[type].disable();
      } else {
        patchObj[type + 'Type'] = 'custom';
        this.snackBar.open('Er is geen thuisadres ingevuld. Vul het adres handmatig in.', 'X', {
          duration: 10000,
        });
      }
    }
    this.rideForm.patchValue(patchObj);
    this.setAddressTypeVisibility(type, val);
  }

  setAddressTypeVisibility(type: 'from' | 'to', value: string) {
    if (type === 'from') {
      this.fromHidden.set(value !== 'custom' && value !== 'home');
      this.standardFromHidden.set(value !== 'addressFromDb');
      this.recentFromHidden.set(value !== 'recent');
    } else {
      this.toHidden.set(value !== 'custom' && value !== 'home');
      this.standardToHidden.set(value !== 'addressFromDb');
      this.recentToHidden.set(value !== 'recent');
    }
  }

  showDropoffTime() {
    return this.rideForm.get('specifyDropoffTime')?.value;
  }

  async retourPreparedRide() {
    let ride = { ...(this.data.ride ?? {}) } as AutomaticallyPlannedRide;
    if (this.rideForm.dirty) {
      await this.save();
      if (!this.savedRide) return;
      ride = { ...ride, ...this.savedRide };
    }

    // 1. Check if we are already a retour, then go to parent
    if (this.data.isRetour && ride.retourParentId) {
      const parentDoc = await getDoc(
        doc(db, `organisations/${this.organisationId}/automaticRides/${ride.retourParentId}`),
      );
      if (parentDoc.exists()) {
        const parentData = { id: parentDoc.id, ...parentDoc.data() } as AutomaticallyPlannedRide;
        this.dialogRef.close();
        this.dialog.open(EditAutomaticRideComponent, {
          panelClass: 'fullscreen-dialog',
          data: {
            ride: parentData,
            rideId: parentData.id,
            isRetour: false,
          },
        });
        return;
      }
    }

    // 2. Check for existing retour of this ride
    let existingRetourId = (ride as any).retourChildId;
    let existingRetourData: any = null;

    if (!existingRetourId) {
      const existingRetours = (
        await getDocs(
          query(
            collection(db, `organisations/${this.organisationId}/automaticRides`),
            where('retourParentId', '==', this.rideId),
          ),
        )
      ).docs;
      if (existingRetours.length > 0) {
        existingRetourId = existingRetours[0].id;
        existingRetourData = { id: existingRetours[0].id, ...existingRetours[0].data() };
      }
    } else {
      const childDoc = await getDoc(
        doc(db, `organisations/${this.organisationId}/automaticRides/${existingRetourId}`),
      );
      if (childDoc.exists()) {
        existingRetourData = { id: childDoc.id, ...childDoc.data() };
      }
    }

    if (existingRetourData) {
      this.dialogRef.close();
      this.dialog.open(EditAutomaticRideComponent, {
        panelClass: 'fullscreen-dialog',
        data: {
          ride: existingRetourData,
          rideId: existingRetourId,
          isRetour: true,
        },
      });
    } else {
      // 3. Create NEW retour
      const newRetourRide = {
        ...ride,
        passenger: structuredClone(ride.passenger),
      };
      newRetourRide.id = undefined;
      newRetourRide.retourParentId = this.rideId;
      newRetourRide.isRetour = true;

      // Switch locations for new retour
      const tempFrom = newRetourRide.passenger.from;
      const tempFromType = newRetourRide.passenger.fromType;
      newRetourRide.passenger.from = newRetourRide.passenger.to;
      newRetourRide.passenger.fromType = newRetourRide.passenger.toType;
      newRetourRide.passenger.to = tempFrom;
      newRetourRide.passenger.toType = tempFromType;
      newRetourRide.fromLongitude = ride.toLongitude;
      newRetourRide.fromLatitude = ride.toLatitude;
      newRetourRide.toLongitude = ride.fromLongitude;
      newRetourRide.toLatitude = ride.fromLatitude;

      this.dialogRef.close();
      this.dialog.open(EditAutomaticRideComponent, {
        panelClass: 'fullscreen-dialog',
        data: {
          ride: newRetourRide,
          rideId: undefined,
          isRetour: true,
        },
      });
    }
  }

  async copyPreparedRide() {
    let ride = { ...(this.data.ride ?? {}) } as AutomaticallyPlannedRide;
    if (this.rideForm.dirty) {
      await this.save();
      if (!this.savedRide) return;
      ride = { ...ride, ...this.savedRide };
    }

    ride.rejected = false;
    ride.planningCompleted = false;

    this.dialogRef.close();
    const copyDialogRef = this.dialog.open(CopyRideComponent, {
      width: '500px',
      data: { ride: ride, planType: PlanType.Automatic },
    });
    copyDialogRef.afterClosed().subscribe((result) => {
      if (result === false) {
        this.dialog.open(EditAutomaticRideComponent, {
          panelClass: 'fullscreen-dialog',
          data: {
            ride: structuredClone(ride),
            rideId: this.rideId,
            isRetour: this.data.isRetour,
          },
        });
      }
    });
  }

  async deletePreparedRide() {
    const dialogRef = this.dialog.open(DeleteRideComponent, {
      width: '500px',
      data: { noDeleteReason: true },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (!result) return;

      const rideRef = doc(db, `organisations/${this.organisationId}/automaticRides/${this.rideId}`);

      if (this.data.ride.parentRideId) {
        const deleteCopiesRef = this.dialog.open(CopyUpdateComponent, {
          width: '600px',
          data: {
            oldRide: structuredClone(this.data.ride),
            delete: true,
            planType: PlanType.Automatic,
          },
        });

        deleteCopiesRef.afterClosed().subscribe(async (copiesDeleted) => {
          if (copiesDeleted === undefined) return;

          await deleteDoc(rideRef);
          this.dialogRef.close();
          this.snackBar.open(
            copiesDeleted ? 'De ritten zijn verwijderd.' : 'De rit is verwijderd.',
            'X',
            { duration: 5000 },
          );
        });
        return;
      }

      await deleteDoc(rideRef);
      this.dialogRef.close();
      this.snackBar.open('De rit is verwijderd.', 'X', { duration: 5000 });
    });
  }
}
